import React, { useState, useEffect, useRef } from "react";
import { PrecisionChatBotIcon } from "assets";

export type LoadingPhase =
  | "jd_upload"
  | "cv_upload"
  | "basic_questions"
  | "generating_jd"
  | "generating_criteria"
  | "technical_questions"
  | "experience_level"
  | "task_ready"
  | "retry"
  | "general";

const loadingMessages: Record<LoadingPhase, string[]> = {
  jd_upload: [
    "Reading through your job description",
    "Almost done, just a moment",
  ],
  cv_upload: [
    "Reviewing your candidates",
    "Matching CVs to your role requirements",
  ],
  basic_questions: [
    "Getting to know the role a little better",
    "Putting together the right questions for you",
  ],
  generating_jd: [
    "Building out your job description",
    "This will only take a moment",
  ],
  generating_criteria: [
    "Thinking through what success looks like",
    "Setting the bar for this role",
  ],
  technical_questions: [
    "Identifying the key skills for this role",
    "Putting together your technical questions",
  ],
  experience_level: [
    "Setting up your screening criteria",
    "Getting ready to review",
  ],
  task_ready: [
    "Preparing your assessment",
    "Almost ready",
  ],
  retry: [
    "Trying again",
    "Give me another moment",
  ],
  general: [
    "Thinking",
    "Working on it",
    "Almost there",
  ],
};

const AnimatedDots = () => (
  <span className="inline">
    <span className="inline-block animate-dot-fade">.</span>
    <span className="inline-block animate-dot-fade [animation-delay:0.3s]">.</span>
    <span className="inline-block animate-dot-fade [animation-delay:0.6s]">.</span>
  </span>
);

interface LoadingIndicatorProps {
  phase?: LoadingPhase;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({ phase = "general" }) => {
  const messages = loadingMessages[phase] || loadingMessages.general;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setCurrentIndex(0);
    setNextIndex(null);
  }, [phase]);

  useEffect(() => {
    if (messages.length <= 1) return;

    timerRef.current = setInterval(() => {
      setNextIndex((prev) => {
        const current = prev !== null ? prev : 0;
        return (current + 1) % messages.length;
      });

      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % messages.length);
        setNextIndex(null);
      }, 1000);
    }, 5000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [messages]);

  return (
    <div className="flex gap-5 items-start max-w-[85%] mt-5 text-[13px]">
      <div className="flex-none w-[30px] h-[30px] flex items-center justify-center">
        <PrecisionChatBotIcon className="w-full h-full object-contain min-w-[35px] min-h-[35px]" />
      </div>
      <div className="flex flex-col gap-2 pt-1">
        <div className="relative min-h-[20px]">
          <span
            className={`text-[#475467] text-[13px] font-normal whitespace-nowrap ${
              nextIndex !== null ? "animate-crossfade-out" : "opacity-100"
            }`}
          >
            {messages[currentIndex]}<AnimatedDots />
          </span>
          {nextIndex !== null && (
            <span className="absolute left-0 top-0 text-[#1D2939] text-[13px] font-normal whitespace-nowrap animate-crossfade-in">
              {messages[nextIndex]}<AnimatedDots />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export { LoadingIndicator, loadingMessages };
